import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import {
  EntityTypeEnum,
  FeaturesEnum,
  NotificationCenterFilter,
  NotificationCenterMessageResponseOutDTO,
  Pageable,
  PageResponseNotificationCenterMessageResponseOutDTO,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, MassActionConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import SvgCheck from '@icons/check.svg?react';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { isNotNullOrUndefined } from '@utils/utils';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import NotificationAdditionalInfo from './NotificationAdditionalInfo.tsx';
import useNotificationCenterNavigation from './useNotificationCenterNavigation.ts';
import { entityTypeLabel, NOTIFICATION_ENTITY_TYPES, notificationTypeLabel } from './notificationCenterLabels.ts';

/** Tri-state the read dropdown stores; resolved to the `isRead` boolean (or omitted) in normalizeFilter. */
export enum ReadFilterStateEnum {
  ALL = 'all',
  UNREAD = 'unread',
  READ = 'read',
}

/** Tri-state the category dropdown stores; resolved to the `isUserMessage` boolean (or omitted) in normalizeFilter. */
export enum CategoryFilterStateEnum {
  ALL = 'all',
  USER_MESSAGES = 'userMessages',
  SYSTEM = 'system',
}

interface NotificationCenterListFilter extends NotificationCenterFilter {
  /** UI-only field driving the read dropdown. Resolved to `isRead` at fetch time. */
  readState?: ReadFilterStateEnum;
  /** UI-only field driving the category dropdown. Resolved to `isUserMessage` at fetch time. */
  categoryState?: CategoryFilterStateEnum;
}

const NotificationCenterList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { markAsReadAndNavigate } = useNotificationCenterNavigation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const {
    listNotificationCenterMessage,
    readNotificationMessages,
    lovGetAllEmailTypes,
    getUserNotificationFilters,
    saveUserNotificationFilters,
    deleteUserFilter,
  } = useContext(ApiContext);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const notificationExportFlag = useSecuredFlag('notification-export', {
    kind: 'action',
    description: 'Export notifications (generic export API)',
    features: [FeaturesEnum.GENERIC_EXPORT_API],
    permissions: [PermissionTypesEnum.PERMISSION_NOTIFICATION_EXPORT],
  });

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: NotificationCenterMessageResponseOutDTO[]; clear: () => void }>(null);
  const [emailTypes, setEmailTypes] = useState<string[]>([]);

  useEffect(() => {
    lovGetAllEmailTypes(data => setEmailTypes(data ?? []));
  }, []);

  const reloadTable = () => {
    tableRef.current?.clear();
    tableRef.current?.reloadData();
  };

  const loadDataApi = (
    filter: NotificationCenterFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<NotificationCenterMessageResponseOutDTO>) => void,
  ) => {
    listNotificationCenterMessage(filter, pageable, (data: PageResponseNotificationCenterMessageResponseOutDTO) => {
      onSuccess({ data: data.data, total: data.total, page: data.page, size: data.size });
    });
  };

  const normalizeFilter = (value?: NotificationCenterListFilter): NotificationCenterListFilter => {
    const result: NotificationCenterListFilter = { ...(value ?? {}) };

    // UI-only tri-states → the booleans the API expects. ALL omits the key entirely so the backend
    // returns both variants instead of matching on `false`. A saved filter comes back holding only the
    // booleans (the API DTO has no room for the UI fields), so fall back to those when they're set.
    const readState = result.readState ?? (isNotNullOrUndefined(result.isRead) ? (result.isRead ? ReadFilterStateEnum.READ : ReadFilterStateEnum.UNREAD) : ReadFilterStateEnum.ALL);
    delete result.readState;
    if (readState === ReadFilterStateEnum.ALL) {
      delete result.isRead;
    } else {
      result.isRead = readState === ReadFilterStateEnum.READ;
    }

    const categoryState =
      result.categoryState ??
      (isNotNullOrUndefined(result.isUserMessage) ? (result.isUserMessage ? CategoryFilterStateEnum.USER_MESSAGES : CategoryFilterStateEnum.SYSTEM) : CategoryFilterStateEnum.ALL);
    delete result.categoryState;
    if (categoryState === CategoryFilterStateEnum.ALL) {
      delete result.isUserMessage;
    } else {
      result.isUserMessage = categoryState === CategoryFilterStateEnum.USER_MESSAGES;
    }

    return result;
  };

  /**
   * Saved filters are persisted as a `NotificationCenterFilter`, which has no place for the UI-only
   * `readState` / `categoryState` fields — saving the raw filter would silently lose the read and
   * category selection. Store the resolved filter instead; normalizeFilter reads the booleans back.
   */
  const saveFilters: typeof saveUserNotificationFilters = (body, onSuccess, hashAuthentication, callbacks) =>
    saveUserNotificationFilters({ ...body, data: normalizeFilter(body.data) }, onSuccess, hashAuthentication, callbacks);

  const markSelectedAsRead = () => {
    const ids = (tableRef.current?.selectedObjects ?? []).map(row => row.id);
    if (ids.length === 0) return;
    readNotificationMessages({ ids }, reloadTable);
  };

  const markAsRead = (row: NotificationCenterMessageResponseOutDTO) => {
    readNotificationMessages({ ids: [row.id] }, reloadTable);
  };

  // Nothing to do for an already read notification — the API has no "mark as unread" counterpart.
  const renderActionsColumn = (row: NotificationCenterMessageResponseOutDTO) =>
    row.isRead ? null : (
      <RingilButton
        mode={'table'}
        icon={<SvgCheck />}
        tooltip={tr('NotificationCenter.markAsRead', 'Mark as read')}
        onClick={() => markAsRead(row)}
        dataTestId={'notification-center-mark-as-read'}
      />
    );

  const renderTypeColumn = (row: NotificationCenterMessageResponseOutDTO) => (
    <div className={'flex flex-col items-start gap-2'}>
      <div className={'flex flex-row items-center gap-2'}>
        <span className={clsx('text-content-primary', !row.isRead && 'font-bold')}>{row.message}</span>
        {!row.isRead && <span className={'h-2 w-2 shrink-0 rounded-full bg-content-brand'} />}
      </div>
      {isNotNullOrUndefined(row.entityType) && (
        <button
          type={'button'}
          onClick={() => markAsReadAndNavigate(row)}
          className={'inline-flex items-center gap-1 rounded border-none bg-content-brandMedium px-2 py-1 text-sm hover:opacity-80'}>
          <span className={'text-content-secondary'}>{entityTypeLabel(tr, row.entityType)}</span>
          {row.applicationId && <span className={'font-medium text-content-brand'}>{row.applicationId}</span>}
        </button>
      )}
    </div>
  );

  const renderPerformedByColumn = (row: NotificationCenterMessageResponseOutDTO) => (
    <div className={'flex flex-col gap-2'}>
      <div className={'text-content-primary'}>{row.fromUserName}</div>
      <div className={'flex flex-col'}>
        <div className={'text-sm text-content-secondary'}>{stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, loggedUser?.defaultTimezoneId)}</div>
        <COTimezone timezoneId={loggedUser?.defaultTimezoneId} />
      </div>
    </div>
  );

  const columnConfig: ColumnConfig<NotificationCenterMessageResponseOutDTO>[] = [
    {
      id: 'type',
      header: 'NotificationCenter.columnType',
      headerDefault: 'Type',
      width: '32%',
      order: 1,
      render: renderTypeColumn,
    },
    {
      id: 'performedBy',
      header: 'NotificationCenter.columnPerformedBy',
      headerDefault: 'Performed by',
      width: '20%',
      order: 2,
      render: renderPerformedByColumn,
    },
    {
      id: 'details',
      header: 'NotificationCenter.columnDetails',
      headerDefault: 'Details',
      width: '42%',
      order: 3,
      render: row => <NotificationAdditionalInfo additionalInfo={row.additionalInfo} />,
    },
    {
      id: 'actions',
      header: 'NotificationCenter.columnActions',
      headerDefault: 'Actions',
      width: '6%',
      order: 4,
      render: renderActionsColumn,
    },
    // The date is not a column of its own — it lives inside "Performed by". A hidden column is the only
    // way to offer it in the sort chip: hidden columns are dropped from the rendered table but still
    // feed the sortable list.
    {
      id: 'createdAt',
      header: 'NotificationCenter.columnDate',
      headerDefault: 'Date',
      field: 'createdAt',
      sortable: true,
      isDefaultSort: true,
      hidden: true,
      order: 5,
      render: () => null,
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('NotificationCenter.filterType', 'Type'),
      label: tr('NotificationCenter.filterType', 'Type'),
      value: ['emailType'],
      options: emailTypes.map(code => ({ label: notificationTypeLabel(tr, code), value: code, active: true })),
      dataTestId: 'notification-center-filter-emailType',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('NotificationCenter.filterEntityType', 'Kind'),
      label: tr('NotificationCenter.filterEntityType', 'Kind'),
      value: ['entityType'],
      options: NOTIFICATION_ENTITY_TYPES.map(entityType => ({ label: entityTypeLabel(tr, entityType), value: entityType, active: true })),
      dataTestId: 'notification-center-filter-entityType',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('NotificationCenter.filterCategory', 'Category'),
      label: tr('NotificationCenter.filterCategory', 'Category'),
      value: ['categoryState'],
      options: [
        { label: tr('NotificationCenter.categoryAll', 'All'), value: CategoryFilterStateEnum.ALL, active: true },
        { label: tr('NotificationCenter.categoryUserMessages', 'User messages'), value: CategoryFilterStateEnum.USER_MESSAGES, active: true },
        { label: tr('NotificationCenter.categorySystem', 'System notifications'), value: CategoryFilterStateEnum.SYSTEM, active: true },
      ],
      dataTestId: 'notification-center-filter-category',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('NotificationCenter.filterRead', 'Read'),
      label: tr('NotificationCenter.filterRead', 'Read'),
      value: ['readState'],
      options: [
        { label: tr('NotificationCenter.readAll', 'All'), value: ReadFilterStateEnum.ALL, active: true },
        { label: tr('NotificationCenter.readUnread', 'Unread'), value: ReadFilterStateEnum.UNREAD, active: true },
        { label: tr('NotificationCenter.readRead', 'Read'), value: ReadFilterStateEnum.READ, active: true },
      ],
      dataTestId: 'notification-center-filter-read',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('NotificationCenter.filterPerformedBy', 'Performed by'),
      label: tr('NotificationCenter.filterPerformedBy', 'Performed by'),
      value: ['performedBy'],
      dataTestId: 'notification-center-filter-performedBy',
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr('NotificationCenter.filterApplicationId', 'ID'),
      label: tr('NotificationCenter.filterApplicationId', 'ID'),
      value: ['applicationId'],
      dataTestId: 'notification-center-filter-applicationId',
    },
  ];

  const massActionConfig: MassActionConfig<NotificationCenterMessageResponseOutDTO>[] = [
    {
      label: tr('NotificationCenter.markAsRead', 'Mark as read'),
      onClick: markSelectedAsRead,
      // Always visible: the table only renders the selection checkboxes when at least one mass action
      // is visible, so gating on the current selection would hide the checkboxes and make selecting
      // anything impossible. Disabled instead until something unread is picked.
      disabled: selected => !selected.some(row => !row.isRead),
    },
  ];

  const renderMobileCard = (row: NotificationCenterMessageResponseOutDTO, checkbox?: JSX.Element | null) => (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex flex-row items-start gap-2'}>
        {checkbox}
        {renderTypeColumn(row)}
      </div>
      {renderPerformedByColumn(row)}
      <NotificationAdditionalInfo additionalInfo={row.additionalInfo} />
      {renderActionsColumn(row)}
    </div>
  );

  return (
    <>
      <BreadCrumbComponent items={[{ label: tr('NotificationCenter.bcNotifications', 'Notifications') }]} />
      <Heading title={tr('NotificationCenter.notifications', 'Notifications')} headingVariant={'h1'} />

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'notification_center_list'}
        normalizeFilter={normalizeFilter}
        massActionConfig={massActionConfig}
        renderMobileCard={isMobile ? renderMobileCard : undefined}
        getSavedFilters={getUserNotificationFilters as never}
        saveFilters={saveFilters as never}
        deleteSavedFilter={deleteUserFilter}
        exportConfig={{ entityType: EntityTypeEnum.NOTIFICATION, entityLabel: 'Notifications', secured: notificationExportFlag }}
      />
    </>
  );
};

export default NotificationCenterList;